import camelcaseKeys from 'camelcase-keys';
import { z } from 'zod';

import {
  LOCATION_STATUS,
  REPORT_STATUS,
  LocationIdSchema,
  GeoLocationSchema,
  PlaceIdSchema
} from '@brightlocal/shared';

const ReportUrlsSchema = z
  .object({
    view_url: z.string(),
    white_label_url: z.string()
  })
  .transform((data) => camelcaseKeys(data));

const ReportSchema = z.object({
  // The tool's record for this location — a report id, or a campaign id for `cb`.
  // `null` until one exists.
  id: z.number().nullable(),
  status: z.nativeEnum(REPORT_STATUS),
  urls: ReportUrlsSchema
});

const LocationLogoSchema = z.object({
  image_id: z.string(),
  image_url: z.string().optional(),
  created_at: z.string().optional(),
  description: z.string().max(100).optional()
});

const LocationPhotoSchema = z.object({
  image_id: z.string(),
  image_url: z.string(),
  created_at: z.string().optional(),
  description: z.string().max(100).optional(),
  order: z.number().int().min(0).max(19).optional()
});

const LocationImagesSchema = z.object({
  logo: LocationLogoSchema.optional(),
  photos: z.array(LocationPhotoSchema).optional()
});

const LocationGooglePlaceSchema = z.object({
  place_id: PlaceIdSchema.nullable(),
  cid: z.string().nullable(),
  business_name: z.string().nullable(),
  telephone: z.string().nullable()
});

export const LocationSchema = z
  .object({
    location_id: LocationIdSchema,
    location_reference: z.string(),
    business_name: z.string(),
    // Details of the Google listing the location is associated with. Null or absent when there is
    // none — e.g. the location was created by hand rather than from a Google place.
    google_place: LocationGooglePlaceSchema.nullish(),
    business_category_id: z.number(),
    status: z.nativeEnum(LOCATION_STATUS),
    country: z.string().optional(),
    language: z.string().optional(),
    client_id: z.number().optional(),
    address: z.object({
      address1: z.string(),
      address2: z.string().optional(),
      region: z.string().optional(),
      region_code: z.string().optional(),
      city: z.string(),
      postcode: z.string().optional()
    }),
    urls: z.object({
      website_url: z.string()
    }),
    reports: z.object({
      ct: ReportSchema.optional(),
      lrt: ReportSchema.optional(),
      lsg: ReportSchema.optional(),
      rm: ReportSchema.optional(),
      gbpa: ReportSchema.optional(),
      // Citation Builder campaign state — always sent. `id` is the campaign id, `null` when there is
      // no campaign; `status` is `not_setup`, `running` / `get_started` (unpaid) or `ok` (purchased).
      cb: ReportSchema
    }),
    telephone: z.string(),
    images: LocationImagesSchema.optional(),
    dashboard_urls: z.object({
      location_url: z.string()
    })
  })
  .transform((data) => camelcaseKeys(data, { deep: true }));

export const CreateLocationFromGooglePlaceSchema = z
  .object({
    location_id: LocationIdSchema
  })
  .transform((data) => camelcaseKeys(data));

export const LocationWithDistanceSchema = z.object({
  distance: z.number(),
  location: LocationSchema
});

export const LocationsSchema = z
  .object({
    total_count: z.number(),
    items: z.array(LocationWithDistanceSchema)
  })
  .transform((data) => camelcaseKeys(data));

export type Location = z.infer<typeof LocationSchema>;
export type LocationWithDistance = z.infer<typeof LocationWithDistanceSchema>;
export type LocationsResponse = z.infer<typeof LocationsSchema>;

/**
 * Validation error schemas for create-location-from-place-id.
 * These schemas expect camelCase keys because the error body is already transformed
 * by BadRequestErrorSchema (via camelcaseKeys) before reaching this point.
 */
const ValidationErrorAddressSchema = z
  .object({
    address1: z.string().optional(),
    address2: z.string().optional(),
    region: z.string().optional(),
    regionCode: z.string().optional(),
    city: z.string().optional(),
    postcode: z.string().optional()
  })
  .optional();

const ValidationErrorUrlsSchema = z
  .object({
    websiteUrl: z.string().optional(),
    menuUrl: z.string().optional(),
    reservationUrl: z.string().optional(),
    orderUrl: z.string().optional(),
    galleryUrl: z.string().optional(),
    eventUrl: z.string().optional()
  })
  .optional();

const ValidationErrorContactSchema = z
  .object({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    mobile: z.string().optional(),
    telephone: z.string().optional(),
    email: z.string().optional(),
    fax: z.string().optional()
  })
  .optional();

export const LocationValidationErrorSchema = z.object({
  general: z.string().optional(),
  locationReference: z.string().optional(),
  country: z.string().optional(),
  businessName: z.string().optional(),
  telephone: z.string().optional(),
  businessCategoryId: z.string().optional(),
  address: ValidationErrorAddressSchema,
  urls: ValidationErrorUrlsSchema,
  contact: ValidationErrorContactSchema,
  geoLocation: GeoLocationSchema,
  language: z.string().optional(),
  timezone: z.string().optional(),
  formationDate: z.string().optional(),
  description: z.string().optional(),
  numEmployees: z.string().optional(),
  paymentMethods: z.string().optional(),
  servicesOrProducts: z.array(z.string()).optional(),
  extraBusinessCategoryIds: z.array(z.number()).optional(),
  openingHours: z.unknown().optional(),
  _form: z.string().optional()
});

export type LocationValidationErrorResponse = z.infer<typeof LocationValidationErrorSchema>;
